import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex, Text } from "@chakra-ui/react";
import { Form as FormikForm, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  RadioGroupInput,
  RadioInputCustomLabel,
  TextInput,
} from "~/components";
import { closeModal } from "~/features/ModalWithState";
import { CheckCircle } from "~/icons";
import { useGatheringsDeclineMutation } from "~/store/api";
import { useAppDispatch } from "~/store/hooks";

const validationSchema = Yup.object({
  feedback: Yup.string().required("Required"),
  feedbackOther: Yup.string().test(
    "requiredIfOther",
    "Required",
    function (value) {
      return this.parent.feedback === "Other" ? !!value : true;
    },
  ),
  slug: Yup.string().required("Required"),
});

const options = [
  {
    label: "Time no longer works",
    value: "Time no longer works",
  },
  {
    label: "Topic no longer of interest",
    value: "Topic no longer of interest",
  },
  {
    label: "Other",
    value: "Other",
  },
];

interface Props {
  slug: string;
  trackers: {
    unregisterFeedbackModalCloseCta: () => void;
    unregisterFeedbackModalSubmitCta: (slug: string) => void;
  };
}

export const UnregisterFeedbackModalContent: FC<Props> = ({
  slug,
  trackers,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [apiGatheringsDecline] = useGatheringsDeclineMutation();

  const initialValues = {
    slug: slug,
    feedback: "",
    feedbackOther: "",
  };

  const handleClose = () => {
    trackers.unregisterFeedbackModalCloseCta();
    dispatch(closeModal());
  };

  const onSubmit = async (): Promise<void> => {
    const { feedback, feedbackOther } = formik.values;
    const explanation = [feedback, feedbackOther]
      .filter((value) => Boolean(value))
      .join(" :: ");

    await apiGatheringsDecline({ slug, explanation });
    trackers.unregisterFeedbackModalSubmitCta(slug);
    handleClose();
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validateOnMount: true,
    validationSchema,
  });

  const isOther = formik.values.feedback === "Other";

  return (
    <FormikProvider value={formik}>
      <FormikForm>
        <Flex direction="column" gap={5} w="full">
          <Flex direction="column" gap={5} w="full">
            <Flex align="center" direction="column" gap={5} w="full">
              <CheckCircle h="30px" w="30px" />
              <Flex
                align="center"
                direction="column"
                gap="10px"
                textAlign="center"
                w="full"
              >
                <Text fontWeight="bold" textAlign="center" variant="th-body1">
                  {t("rsvp.unregister.feedback.heading")}
                </Text>
                <Text variant="th-body2">
                  {t("rsvp.unregister.feedback.prompt")}
                </Text>
              </Flex>
            </Flex>
            <Flex align="center" direction="column" gap="10px" w="full">
              <RadioGroupInput
                Component={RadioInputCustomLabel}
                labelProps={{
                  variant: "th-body2",
                }}
                gap={4}
                name="feedback"
                options={options}
              />
              {isOther && (
                <TextInput
                  fontSize="14px"
                  minH="unset"
                  name="feedbackOther"
                  placeholder={t("rsvp.unregister.feedback.otherPlaceholder")}
                  w="full"
                />
              )}
            </Flex>
          </Flex>
          <Button
            size="th-md"
            type="submit"
            variant="th-primary"
            isDisabled={!formik.isValid}
          >
            {t("rsvp.unregister.feedback.cta")}
          </Button>
        </Flex>
      </FormikForm>
    </FormikProvider>
  );
};
